/**
 * useTransactionFilters Hook
 * 
 * Custom hook for managing transaction filters (date range, order ID, location) and URL sync.
 */

import * as React from "react";
import { useLocation } from "wouter";

export function useTransactionFilters(
  lockedProvince?: string,
  onFilterChange?: () => void,
  lockedCity?: string
) {
  const [location, setLocation] = useLocation();
  
  // Date range filters
  const [fromDate, setFromDate] = React.useState<Date | undefined>(undefined);
  const [toDate, setToDate] = React.useState<Date | undefined>(undefined);
  
  // Order ID search
  const [orderIdSearch, setOrderIdSearch] = React.useState("");
  
  // Location filters - if lockedProvince is provided, always use it
  const [province, setProvince] = React.useState<string | undefined>(lockedProvince);
  const [city, setCity] = React.useState<string | undefined>(lockedCity);

  // Sort filters
  const [sortBy, setSortBy] = React.useState<"createdAt" | "status" | "province" | "kabkot" | "_id">("createdAt");
  const [sortOrder, setSortOrder] = React.useState<"asc" | "desc">("desc");

  // Use refs to track latest values for callbacks
  const fromDateRef = React.useRef<Date | undefined>(undefined);
  const toDateRef = React.useRef<Date | undefined>(undefined);
  const orderIdSearchRef = React.useRef<string>("");
  const provinceRef = React.useRef<string | undefined>(undefined);
  const cityRef = React.useRef<string | undefined>(undefined);
  const sortByRef = React.useRef<"createdAt" | "status" | "province" | "kabkot" | "_id">("createdAt");
  const sortOrderRef = React.useRef<"asc" | "desc">("desc");

  // Keep refs in sync with state
  React.useEffect(() => {
    fromDateRef.current = fromDate;
    toDateRef.current = toDate;
    orderIdSearchRef.current = orderIdSearch;
    provinceRef.current = province;
    cityRef.current = city;
    sortByRef.current = sortBy;
    sortOrderRef.current = sortOrder;
  }, [fromDate, toDate, orderIdSearch, province, city, sortBy, sortOrder]);

  // Read filters from URL query string on mount
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const fromDateParam = urlParams.get("fromDate");
    const toDateParam = urlParams.get("toDate");
    const orderIdParam = urlParams.get("orderId") || "";
    // If lockedProvince exists, always use it (override URL param)
    const provinceParam = lockedProvince || urlParams.get("province") || undefined;
    const cityParam = lockedCity || urlParams.get("city") || undefined;
    const sortByParam = urlParams.get("sortBy") as "createdAt" | "status" | "province" | "kabkot" | "_id" | null;
    const sortOrderParam = urlParams.get("sortOrder") as "asc" | "desc" | null;
    
    // Parse date params
    if (fromDateParam) {
      const parsedDate = new Date(fromDateParam);
      if (!isNaN(parsedDate.getTime())) {
        setFromDate(parsedDate);
      }
    }
    if (toDateParam) {
      const parsedDate = new Date(toDateParam);
      if (!isNaN(parsedDate.getTime())) {
        setToDate(parsedDate);
      }
    }
    
    setOrderIdSearch(orderIdParam);
    // Always set locked province if it exists
    setProvince(lockedProvince || provinceParam);
    setCity(lockedCity || cityParam);

    // Parse sort params
    if (sortByParam && ["createdAt", "status", "province", "kabkot", "_id"].includes(sortByParam)) {
      setSortBy(sortByParam);
    }
    if (sortOrderParam && ["asc", "desc"].includes(sortOrderParam)) {
      setSortOrder(sortOrderParam);
    }
    
    // Initialize refs
    fromDateRef.current = fromDateParam ? new Date(fromDateParam) : undefined;
    toDateRef.current = toDateParam ? new Date(toDateParam) : undefined;
    orderIdSearchRef.current = orderIdParam;
    provinceRef.current = lockedProvince || provinceParam;
    cityRef.current = lockedCity || cityParam;
    sortByRef.current = sortByParam && ["createdAt", "status", "province", "kabkot", "_id"].includes(sortByParam) ? sortByParam : "createdAt";
    sortOrderRef.current = sortOrderParam && ["asc", "desc"].includes(sortOrderParam) ? sortOrderParam : "desc";
  }, [lockedProvince, lockedCity]);

  // Update URL query string when filters change
  const updateFiltersInUrl = React.useCallback((
    fromDate?: Date,
    toDate?: Date,
    orderId?: string,
    province?: string,
    city?: string,
    sortBy?: "createdAt" | "status" | "province" | "kabkot" | "_id",
    sortOrder?: "asc" | "desc"
  ) => {
    const urlParams = new URLSearchParams(window.location.search);
    
    // Update date params
    if (fromDate) {
      const dateStr = fromDate.toISOString().split("T")[0];
      if (dateStr) {
        urlParams.set("fromDate", dateStr);
      } else {
        urlParams.delete("fromDate");
      }
    } else {
      urlParams.delete("fromDate");
    }
    
    if (toDate) {
      const dateStr = toDate.toISOString().split("T")[0];
      if (dateStr) {
        urlParams.set("toDate", dateStr);
      } else {
        urlParams.delete("toDate");
      }
    } else {
      urlParams.delete("toDate");
    }
    
    // Update order ID param
    if (orderId && orderId.trim()) {
      urlParams.set("orderId", orderId.trim());
    } else {
      urlParams.delete("orderId");
    }
    
    // Update location params - use selected province, but fallback to lockedProvince if no province selected
    const provinceToSet = province || lockedProvince;
    if (provinceToSet && provinceToSet.trim()) {
      urlParams.set("province", provinceToSet.trim());
    } else {
      urlParams.delete("province");
    }
    
    const cityToSet = (city && city.trim()) || (lockedCity && lockedCity.trim());
    if (cityToSet) {
      urlParams.set("city", cityToSet);
    } else {
      urlParams.delete("city");
    }

    // Update sort params
    if (sortBy && sortBy !== "createdAt") {
      urlParams.set("sortBy", sortBy);
    } else {
      urlParams.delete("sortBy");
    }
    if (sortOrder && sortOrder !== "desc") {
      urlParams.set("sortOrder", sortOrder);
    } else {
      urlParams.delete("sortOrder");
    }
    
    // Remove pagination params when filters change
    urlParams.delete("cursor");
    urlParams.delete("direction");
    
    const newSearch = urlParams.toString();
    const basePath = location.split("?")[0] || "";
    const newUrl = newSearch ? `${basePath}?${newSearch}` : basePath;
    setLocation(newUrl, { replace: true });
    
    // Notify that filters have changed (e.g., to reset cursor)
    if (onFilterChange) {
      onFilterChange();
    }
  }, [location, setLocation, lockedProvince, lockedCity, onFilterChange]);

  // Update URL with pagination cursor while preserving filters
  const updateUrlWithCursor = React.useCallback((cursor: string | null, direction: "next" | "previous") => {
    const urlParams = new URLSearchParams();
    
    // Preserve filters
    if (fromDateRef.current) {
      const dateStr = fromDateRef.current.toISOString().split("T")[0];
      if (dateStr) {
        urlParams.set("fromDate", dateStr);
      }
    }
    if (toDateRef.current) {
      const dateStr = toDateRef.current.toISOString().split("T")[0];
      if (dateStr) {
        urlParams.set("toDate", dateStr);
      }
    }
    if (orderIdSearchRef.current && orderIdSearchRef.current.trim()) {
      urlParams.set("orderId", orderIdSearchRef.current.trim());
    }
    // Preserve location filters - use selected province, but fallback to lockedProvince if no province selected
    const provinceToPreserve = provinceRef.current || lockedProvince;
    if (provinceToPreserve && provinceToPreserve.trim()) {
      urlParams.set("province", provinceToPreserve.trim());
    }
    const cityToPreserve =
      (cityRef.current && cityRef.current.trim()) || (lockedCity && lockedCity.trim());
    if (cityToPreserve) {
      urlParams.set("city", cityToPreserve);
    }
    if (sortByRef.current && sortByRef.current !== "createdAt") {
      urlParams.set("sortBy", sortByRef.current);
    }
    if (sortOrderRef.current && sortOrderRef.current !== "desc") {
      urlParams.set("sortOrder", sortOrderRef.current);
    }
    
    // Add cursor for pagination
    if (cursor) {
      urlParams.set("cursor", cursor);
      urlParams.set("direction", direction);
    }
    
    const newSearch = urlParams.toString();
    const basePath = location.split("?")[0] || "";
    const newUrl = newSearch ? `${basePath}?${newSearch}` : basePath;
    setLocation(newUrl, { replace: true });
  }, [location, setLocation, lockedProvince, lockedCity]);

  const clearFilters = React.useCallback(() => {
    setFromDate(undefined);
    setToDate(undefined);
    setOrderIdSearch("");
    // Preserve lockedProvince if it exists
    setProvince(lockedProvince);
    setCity(lockedCity);
    setSortBy("createdAt");
    setSortOrder("desc");
    // Preserve lockedProvince in URL when clearing
    updateFiltersInUrl(undefined, undefined, "", lockedProvince, lockedCity, "createdAt", "desc");
  }, [updateFiltersInUrl, lockedProvince, lockedCity]);

  const updateSort = React.useCallback((field: "createdAt" | "status" | "province" | "kabkot" | "_id", order: "asc" | "desc") => {
    setSortBy(field);
    setSortOrder(order);

    // Update URL with sort params
    const urlParams = new URLSearchParams(window.location.search);
    if (field !== "createdAt") {
      urlParams.set("sortBy", field);
    } else {
      urlParams.delete("sortBy");
    }
    if (order !== "desc") {
      urlParams.set("sortOrder", order);
    } else {
      urlParams.delete("sortOrder");
    }
    urlParams.delete("cursor");
    urlParams.delete("direction");

    const newSearch = urlParams.toString();
    const basePath = location.split("?")[0] || "";
    const newUrl = newSearch ? `${basePath}?${newSearch}` : basePath;
    setLocation(newUrl, { replace: true });
    
    // Notify that filters have changed (e.g., to reset cursor)
    if (onFilterChange) {
      onFilterChange();
    }
  }, [location, setLocation, onFilterChange]);

  return {
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    orderIdSearch,
    setOrderIdSearch,
    province,
    setProvince,
    city,
    setCity,
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    clearFilters,
    updateFiltersInUrl,
    updateUrlWithCursor,
    updateSort,
    // Refs for accessing latest values
    fromDateRef,
    toDateRef,
    orderIdSearchRef,
    provinceRef,
    cityRef,
    sortByRef,
    sortOrderRef,
  };
}

